import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card } from '@/components/ui/shadcn/card';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Lead, LeadOffer, LeadOfferFile, LeadOfferLine } from '@/types';
import type { OfferLineFormData } from '@/types/forms/leads';
import { router, useForm } from '@inertiajs/react';
import { AlertTriangle, Download, File, Plus, Trash2, Upload, X } from 'lucide-react';
import React, { FormEventHandler, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import OfferLinesEditor from './OfferLinesEditor';

interface EditOfferModalProps {
    lead: Lead;
    offer: LeadOffer;
    isOpen: boolean;
    onClose: () => void;
}

const updateOfferSchema = (t: (key: string, fallback?: string) => string) =>
    z.object({
        sales_price: z
            .string()
            .min(1, t('leads.forms.edit_offer.validation.sales_price_required', 'Sales price is required'))
            .refine(
                (value) => {
                    const numeric = Number(value);
                    return !Number.isNaN(numeric) && numeric >= 0;
                },
                t('leads.forms.edit_offer.validation.sales_price_invalid', 'Enter a valid sales price'),
            ),
        description: z
            .string()
            .max(5000, t('leads.forms.edit_offer.validation.description_max', 'Description must be less than 5000 characters'))
            .optional(),
    });

/**
 * Costs are rough working figures rather than accounted line items, so they
 * carry only a description and an amount excluding VAT.
 */
type OfferCostLineFormData = {
    description: string;
    amount: string;
    date_incurred: Date | null;
    id?: string; // UUID - For existing cost lines
};

/** Maps an offer's stored lines onto the editor's all-strings form shape. */
function offerLinesToFormData(offer: LeadOffer): OfferLineFormData[] {
    const stored = offer.lines || (offer as unknown as { offer_lines?: LeadOfferLine[] }).offer_lines || [];

    return stored.map((line) => ({
        id: line.id,
        code: line.code ?? '',
        name: line.name,
        quantity: String(line.quantity ?? ''),
        unit: line.unit ?? '',
        discount: line.discount ?? '',
        unit_price: String(line.unit_price ?? ''),
        line_total: String(line.line_total ?? ''),
        // Stored totals came from the source document; leave them be.
        touchedTotal: true,
    }));
}

/** The picker hands back a yyyy-MM-dd string; parse it as a local date, not UTC midnight. */
function parseDateValue(value: string): Date | null {
    if (!value.trim()) {
        return null;
    }

    const parsed = new Date(`${value}T00:00:00`);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export default function EditOfferModal({ lead, offer, isOpen, onClose }: EditOfferModalProps) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    // App-wide setting rather than a page prop: this modal is mounted from pages
    // that do not all pass one, which silently fell back to a hardcoded 10.
    const approvalThreshold = settings.leadOfferApprovalThreshold;
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [profitWarning, setProfitWarning] = useState<{ show: boolean; percentage: number }>({ show: false, percentage: 0 });

    // Initialize cost lines from offer - use useEffect to update when offer changes
    const [costLines, setCostLines] = useState<OfferCostLineFormData[]>(() => {
        // Initialize with existing cost lines from offer
        if (offer.costLines && offer.costLines.length > 0) {
            return offer.costLines.map((cost) => ({
                id: cost.id,
                description: cost.description,
                amount: cost.amount.toString(),
                date_incurred: cost.date_incurred ? new Date(cost.date_incurred) : null,
            }));
        }
        return [];
    });

    // Offer lines, seeded and resynced the same way cost lines are above.
    const [lines, setLines] = useState<OfferLineFormData[]>(() => offerLinesToFormData(offer));

    useEffect(() => {
        if (isOpen) {
            setLines(offerLinesToFormData(offer));
        }
    }, [isOpen, offer]);

    // Update cost lines when offer changes (e.g., when modal opens with fresh data)
    useEffect(() => {
        if (isOpen && offer.costLines) {
            const initialCostLines = offer.costLines.map((cost) => ({
                id: cost.id,
                description: cost.description,
                amount: cost.amount.toString(),
                date_incurred: cost.date_incurred ? new Date(cost.date_incurred) : null,
            }));
            setCostLines(initialCostLines);
        }
    }, [isOpen, offer.costLines]);
    const [newCostData, setNewCostData] = useState<OfferCostLineFormData>({
        description: '',
        amount: '',
        date_incurred: null,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const { data, setData, processing, errors, reset } = useForm<{
        sales_price: string;
        description: string;
        offer_number: string;
        offer_date: string;
        cost_lines?: Array<{
            description: string;
            amount: string;
            date_incurred: string;
        }>;
    }>({
        sales_price: offer.sales_price.toString(),
        description: offer.description || '',
        offer_number: offer.offer_number || '',
        offer_date: offer.offer_date ? String(offer.offer_date).slice(0, 10) : '',
    });

    // Calculate total costs from offer cost lines
    const calculateTotalOfferCosts = (): number => {
        return costLines.reduce((sum, cost) => {
            const amount = Number(cost.amount) || 0;
            return sum + amount;
        }, 0);
    };

    // Calculate profit percentage using offer costs
    const calculateProfitPercentage = (salesPrice: number): number => {
        const totalCosts = calculateTotalOfferCosts();
        if (salesPrice <= 0) return 0;
        const profit = salesPrice - totalCosts;
        return (profit / salesPrice) * 100;
    };

    // Recalculate profit when costLines or sales_price changes
    useEffect(() => {
        const numericValue = Number(data.sales_price) || 0;
        if (numericValue > 0) {
            // Calculate total from ALL cost lines (existing + new)
            const totalCosts = costLines.reduce((sum, cost) => {
                const amount = Number(cost.amount) || 0;
                return sum + amount;
            }, 0);
            const profit = numericValue - totalCosts;
            const percentage = numericValue > 0 ? (profit / numericValue) * 100 : 0;
            setProfitWarning({
                show: percentage < approvalThreshold,
                percentage,
            });
        } else {
            setProfitWarning({ show: false, percentage: 0 });
        }
    }, [costLines, data.sales_price, approvalThreshold]);

    const updateProfitCalculation = () => {
        const numericValue = Number(data.sales_price) || 0;
        if (numericValue > 0) {
            // Calculate total from ALL cost lines (existing + new)
            const totalCosts = costLines.reduce((sum, cost) => {
                const amount = Number(cost.amount) || 0;
                return sum + amount;
            }, 0);
            const profit = numericValue - totalCosts;
            const percentage = numericValue > 0 ? (profit / numericValue) * 100 : 0;
            setProfitWarning({
                show: percentage < approvalThreshold,
                percentage,
            });
        } else {
            setProfitWarning({ show: false, percentage: 0 });
        }
    };

    const handleSalesPriceChange = (value: string) => {
        setData('sales_price', value);
        updateProfitCalculation();
        clearValidationError('sales_price');
    };

    // Handle adding new cost line
    const handleAddCostLine = () => {
        if (!newCostData.description || !newCostData.amount || !newCostData.date_incurred) {
            toast.error(t('leads.forms.edit_offer.validation.fill_all_cost_fields', 'Please fill in all cost line fields'));
            return;
        }
        const updatedCostLines = [...costLines, { ...newCostData }];
        setCostLines(updatedCostLines);
        setNewCostData({
            description: '',
            amount: '',
            date_incurred: null,
        });
        setTimeout(() => {
            const totalCosts = updatedCostLines.reduce((sum, cost) => {
                const amount = Number(cost.amount) || 0;
                return sum + amount;
            }, 0);
            const numericValue = Number(data.sales_price) || 0;
            if (numericValue > 0) {
                const profit = numericValue - totalCosts;
                const percentage = (profit / numericValue) * 100;
                setProfitWarning({
                    show: percentage < approvalThreshold,
                    percentage,
                });
            }
        }, 0);
    };

    // Handle removing cost line
    const handleRemoveCostLine = (index: number) => {
        setCostLines(costLines.filter((_, i) => i !== index));
        updateProfitCalculation();
    };

    // Handle updating existing cost line
    const handleUpdateCostLine = (index: number, field: keyof OfferCostLineFormData, value: string | Date | null) => {
        const updatedCostLines = [...costLines];
        updatedCostLines[index] = { ...updatedCostLines[index], [field]: value };
        setCostLines(updatedCostLines);
        updateProfitCalculation();
    };

    const clearValidationError = (field: string) => {
        setValidationErrors((prev) => {
            if (!prev[field]) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();

        const result = updateOfferSchema(t).safeParse(data);

        if (!result.success) {
            const fieldErrors: Record<string, string> = {};
            result.error.issues.forEach((issue) => {
                const field = issue.path[0];
                if (field) {
                    fieldErrors[field as string] = issue.message;
                }
            });
            setValidationErrors(fieldErrors);
            toast.error(t('leads.forms.edit_offer.validation.resolve_errors', 'Please resolve the highlighted errors.'));
            return;
        }

        setValidationErrors({});

        // Prepare cost lines data - ensure ALL cost lines (existing + new) are included
        const costLinesData = costLines.map((costLine) => ({
            description: costLine.description,
            amount: costLine.amount,
            date_incurred: costLine.date_incurred ? formatDate(costLine.date_incurred) : '',
        }));

        // Set cost_lines in form data, then submit
        // We need to ensure cost_lines is included, so we'll merge it with the current form data
        const formDataWithCostLines = {
            ...data,
            cost_lines: costLinesData,
            lines: lines
                .filter((line) => line.name.trim())
                .map((line, index) => ({
                    position: index + 1,
                    code: line.code,
                    name: line.name,
                    quantity: line.quantity || '1',
                    unit: line.unit,
                    discount: line.discount,
                    unit_price: line.unit_price || '0',
                    line_total: line.line_total || '0',
                })),
        };

        // Use router.put directly with the merged data to ensure cost_lines are included
        setIsSubmitting(true);
        router.put(`/leads/${lead.id}/offers/${offer.id}`, formDataWithCostLines, {
            onSuccess: () => {
                // If there are new files to upload, upload them separately
                if (selectedFiles.length > 0) {
                    const formData = new FormData();
                    selectedFiles.forEach((file) => {
                        formData.append('files[]', file);
                    });

                    router.post(`/leads/${lead.id}/offers/${offer.id}/files`, formData, {
                        forceFormData: true,
                        onSuccess: () => {
                            setIsSubmitting(false);
                            setSelectedFiles([]);
                            onClose();
                            reset();
                            setProfitWarning({ show: false, percentage: 0 });
                            toast.success(t('leads.forms.edit_offer.messages.updated_with_files', 'Offer and files updated successfully'));
                            router.reload();
                        },
                        onError: () => {
                            setIsSubmitting(false);
                            toast.error(t('leads.forms.edit_offer.messages.updated_files_failed', 'Offer updated but files failed to upload'));
                            router.reload();
                        },
                    });
                } else {
                    setIsSubmitting(false);
                    onClose();
                    reset();
                    setProfitWarning({ show: false, percentage: 0 });
                    toast.success(t('leads.forms.edit_offer.messages.updated_successfully', 'Offer updated successfully'));
                    router.reload();
                }
            },
            onError: () => {
                setIsSubmitting(false);
                toast.error(t('leads.forms.edit_offer.messages.failed_to_update', 'Failed to update offer'));
            },
        });
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);

        // Validate file types and sizes
        const validFiles: File[] = [];
        const invalidFiles: string[] = [];

        files.forEach((file) => {
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ];
            const maxSize = 10 * 1024 * 1024; // 10MB
            const existingFilesCount = (offer.files?.length || 0) + selectedFiles.length;

            if (!allowedTypes.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|xls|xlsx)$/i)) {
                invalidFiles.push(`${file.name} - ${t('leads.forms.edit_offer.validation.file_invalid_type', 'Invalid file type')}`);
            } else if (file.size > maxSize) {
                invalidFiles.push(`${file.name} - ${t('leads.forms.edit_offer.validation.file_too_large', 'File too large (max 10MB)')}`);
            } else if (existingFilesCount + validFiles.length >= 10) {
                invalidFiles.push(`${file.name} - ${t('leads.forms.edit_offer.validation.file_max_count', 'Maximum 10 files allowed')}`);
            } else {
                validFiles.push(file);
            }
        });

        if (invalidFiles.length > 0) {
            toast.error(`${t('leads.forms.edit_offer.validation.files_rejected', 'Some files were rejected:')}\n${invalidFiles.join('\n')}`);
        }

        setSelectedFiles((prev) => [...prev, ...validFiles]);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes: number): string => {
        if (!bytes) return '0 Bytes';
        const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        const size = bytes / Math.pow(1024, i);
        return `${size.toFixed(size >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
    };

    const handleDownload = (file: LeadOfferFile) => {
        const url = `/leads/${lead.id}/offers/${offer.id}/files/${file.id}/download`;
        window.open(url, '_blank');
    };

    const handleClose = () => {
        onClose();
        reset();
        setCostLines(() => {
            // Reset to original offer cost lines
            if (offer.costLines && offer.costLines.length > 0) {
                return offer.costLines.map((cost) => ({
                    id: cost.id,
                    description: cost.description,
                    amount: cost.amount.toString(),
                    date_incurred: cost.date_incurred ? new Date(cost.date_incurred) : null,
                }));
            }
            return [];
        });
        setNewCostData({
            description: '',
            amount: '',
            date_incurred: null,
        });
        setSelectedFiles([]);
        setProfitWarning({ show: false, percentage: 0 });
        setValidationErrors({});
    };

    const totalCosts = calculateTotalOfferCosts();
    const salesPrice = Number(data.sales_price) || 0;
    const profit = salesPrice - totalCosts;
    const profitPercentage = salesPrice > 0 ? calculateProfitPercentage(salesPrice) : 0;

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleClose} variant="sheet">
            {/* Widest size: the offer lines table is eight columns across. */}
            <ResponsiveModalContent size="2xl">
                <ResponsiveModalHeader>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <ResponsiveModalTitle>{t('leads.forms.edit_offer.title', 'Edit Offer')}</ResponsiveModalTitle>
                            <ResponsiveModalDescription className="mt-2">
                                {t(
                                    'leads.forms.edit_offer.description',
                                    'Update your offer details and costs. Offers with profit margin below :threshold% will require manager approval.',
                                ).replace(':threshold', approvalThreshold.toString())}
                            </ResponsiveModalDescription>
                        </div>
                        {offer.status && offer.status !== 'pending' && (
                            <Badge
                                className={
                                    offer.status === 'approved'
                                        ? 'bg-success-subtle text-success-subtle-foreground'
                                        : 'bg-danger-subtle text-danger-subtle-foreground'
                                }
                            >
                                {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                            </Badge>
                        )}
                    </div>
                </ResponsiveModalHeader>

                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-6">
                        {/* The offer and cost lines need the room: the left column is a few
                            fields and a summary, the right one is two editable tables. */}
                        <div className="grid grid-cols-1 gap-8 py-6 lg:grid-cols-5">
                            {/* Left Column */}
                            <div className="order-1 space-y-6 lg:col-span-2">
                                {/* The supplier's own reference, and what offers get
                                searched by. */}
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <Label htmlFor="offer_number" className="text-base font-semibold">
                                            {t('leads.forms.edit_offer.labels.offer_number', 'Offer number')}
                                        </Label>
                                        <Input
                                            id="offer_number"
                                            value={data.offer_number}
                                            onChange={(e) => setData('offer_number', e.target.value)}
                                            placeholder={t('leads.forms.edit_offer.placeholders.offer_number', 'e.g. 104515')}
                                            className="mt-2"
                                            disabled={offer.status !== 'pending'}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="offer_date" className="text-base font-semibold">
                                            {t('leads.forms.edit_offer.labels.offer_date', 'Offer date')}
                                        </Label>
                                        <div className="mt-2">
                                            <DatePicker
                                                value={data.offer_date}
                                                onValueChange={(value) => setData('offer_date', value)}
                                                placeholder={t('common.date', 'Date')}
                                                disabled={offer.status !== 'pending'}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="sales_price" className="text-base font-semibold">
                                        {t('leads.forms.edit_offer.labels.sales_price', 'Sales Price')} *
                                    </Label>
                                    <div className="relative mt-2">
                                        <Input
                                            id="sales_price"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={data.sales_price}
                                            onChange={(e) => handleSalesPriceChange(e.target.value)}
                                            placeholder={t('leads.forms.edit_offer.placeholders.amount', '0.00')}
                                            className={`text-lg font-semibold ${validationErrors.sales_price || errors.sales_price ? 'border-destructive' : ''}`}
                                            disabled={offer.status !== 'pending'}
                                        />
                                        <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2">€</span>
                                    </div>
                                    {validationErrors.sales_price && <p className="text-destructive mt-1 text-sm">{validationErrors.sales_price}</p>}
                                    {errors.sales_price && <p className="text-destructive mt-1 text-sm">{errors.sales_price}</p>}
                                </div>

                                <div>
                                    <Label htmlFor="description" className="text-base font-semibold">
                                        {t('leads.forms.edit_offer.labels.description', 'Description')}
                                    </Label>
                                    <Textarea
                                        id="description"
                                        value={data.description}
                                        onChange={(e) => {
                                            setData('description', e.target.value);
                                            clearValidationError('description');
                                        }}
                                        placeholder={t('leads.forms.edit_offer.placeholders.description', 'Describe what is being offered...')}
                                        className={`mt-2 ${validationErrors.description || errors.description ? 'border-destructive' : ''}`}
                                        rows={4}
                                        maxLength={5000}
                                        disabled={offer.status !== 'pending'}
                                    />
                                    {validationErrors.description && <p className="text-destructive mt-1 text-sm">{validationErrors.description}</p>}
                                    {errors.description && <p className="text-destructive mt-1 text-sm">{errors.description}</p>}
                                </div>

                                {/* Summary Card */}
                                <Card className="bg-muted/50 space-y-3 p-4">
                                    <h3 className="text-muted-foreground mb-4 text-sm font-semibold">
                                        {t('leads.forms.edit_offer.labels.summary', 'Summary')}
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex items-baseline justify-between">
                                            <span className="text-muted-foreground text-sm">
                                                {t('leads.forms.edit_offer.summary.total_costs', 'Total Costs')}
                                            </span>
                                            <span className="font-semibold">{formatCurrency(totalCosts)}</span>
                                        </div>
                                        <div className="bg-border h-px" />
                                        <div className="flex items-baseline justify-between">
                                            <span className="text-muted-foreground text-sm">
                                                {t('leads.forms.edit_offer.summary.profit', 'Profit')}
                                            </span>
                                            <span
                                                className={`text-lg font-semibold ${profit > 0 ? 'text-success-subtle-foreground' : 'text-destructive'}`}
                                            >
                                                {formatCurrency(profit)}
                                            </span>
                                        </div>
                                        <div className="bg-border h-px" />
                                        <div className="flex items-baseline justify-between">
                                            <span className="text-muted-foreground text-sm">
                                                {t('leads.forms.edit_offer.summary.profit_margin', 'Profit Margin')}
                                            </span>
                                            <span
                                                className={`text-lg font-semibold ${profitPercentage >= approvalThreshold ? 'text-success-subtle-foreground' : 'text-warning-subtle-foreground'}`}
                                            >
                                                {profitPercentage.toFixed(2)}%
                                            </span>
                                        </div>

                                        {profitWarning.show && (
                                            <div className="border-warning-subtle bg-warning-subtle mt-4 flex gap-2 rounded-lg border p-3">
                                                <AlertTriangle className="text-warning-subtle-foreground mt-0.5 h-4 w-4 flex-shrink-0" />
                                                <div className="text-warning-subtle-foreground text-sm">
                                                    {t(
                                                        'leads.forms.edit_offer.approval.required',
                                                        'Requires approval (margin < :threshold%)',
                                                    ).replace(':threshold', approvalThreshold.toString())}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            </div>

                            {/* Right Column - Cost Management */}
                            <div className="order-2 min-w-0 space-y-4 lg:col-span-3">
                                <div>
                                    <Label className="mb-4 block text-base font-semibold">{t('leads.offer_lines.heading', 'Offer lines')}</Label>
                                    <OfferLinesEditor lines={lines} onChange={setLines} disabled={offer.status !== 'pending'} />
                                </div>

                                <div>
                                    <Label className="mb-4 block text-base font-semibold">
                                        {t('leads.forms.edit_offer.labels.cost_lines', 'Cost Lines')}
                                    </Label>

                                    {/* Cost Lines Section */}
                                    <div className="space-y-4">
                                        {/* Header labels read as a table header, so they only make
                                            sense once the row is actually a row. */}
                                        <div className="hidden w-full items-center gap-2 md:flex md:flex-row">
                                            <div className="flex-1">
                                                <Label className="text-muted-foreground text-xs">{t('common.description', 'Description')}</Label>
                                            </div>
                                            <div className="w-40">
                                                <Label className="text-muted-foreground text-xs">
                                                    {t('leads.forms.edit_offer.labels.cost_amount', 'Amount excl. VAT')} (€)
                                                </Label>
                                            </div>
                                            <div className="w-36">
                                                <Label className="text-muted-foreground text-xs">{t('common.date', 'Date')}</Label>
                                            </div>
                                            <div className="w-10"></div>
                                        </div>

                                        {/* Existing Cost Lines */}
                                        <div className="max-h-64 space-y-2 overflow-y-auto">
                                            {costLines.map((costLine, index) => (
                                                <div key={index} className="flex w-full flex-col gap-2 md:flex-row md:items-center">
                                                    <div className="w-full md:flex-1">
                                                        <Input
                                                            value={costLine.description}
                                                            onChange={(e) => handleUpdateCostLine(index, 'description', e.target.value)}
                                                            placeholder={t(
                                                                'leads.forms.edit_offer.placeholders.cost_description',
                                                                'Cost description',
                                                            )}
                                                            className="text-sm"
                                                            disabled={offer.status !== 'pending'}
                                                        />
                                                    </div>
                                                    <div className="w-full md:w-32">
                                                        <div className="relative">
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={costLine.amount}
                                                                onChange={(e) => handleUpdateCostLine(index, 'amount', e.target.value)}
                                                                placeholder={t('leads.forms.edit_offer.placeholders.amount', '0.00')}
                                                                className="pr-8 text-sm font-semibold"
                                                                disabled={offer.status !== 'pending'}
                                                            />
                                                            <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 text-sm">
                                                                €
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="w-full md:w-36">
                                                        <DatePicker
                                                            value={costLine.date_incurred ? formatDate(costLine.date_incurred) : ''}
                                                            onValueChange={(value) =>
                                                                handleUpdateCostLine(index, 'date_incurred', parseDateValue(value))
                                                            }
                                                            placeholder={t('common.date', 'Date')}
                                                            disabled={offer.status !== 'pending'}
                                                        />
                                                    </div>
                                                    <div className="flex w-full items-center justify-end md:w-10">
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveCostLine(index)}
                                                            className="text-muted-foreground hover:text-destructive p-1 transition-colors"
                                                            aria-label={t('leads.forms.edit_offer.aria.remove_cost_line', 'Remove cost line')}
                                                            disabled={offer.status !== 'pending'}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Add New Cost Line */}
                                        {offer.status === 'pending' && (
                                            <div className="space-y-2 border-t pt-2">
                                                <div className="flex w-full flex-col gap-2 md:flex-row md:items-center">
                                                    <div className="w-full md:flex-1">
                                                        <Input
                                                            value={newCostData.description}
                                                            onChange={(e) => setNewCostData({ ...newCostData, description: e.target.value })}
                                                            placeholder={t(
                                                                'leads.forms.edit_offer.placeholders.cost_description',
                                                                'Cost description',
                                                            )}
                                                            className="text-sm"
                                                        />
                                                    </div>
                                                    <div className="w-full md:w-32">
                                                        <div className="relative">
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={newCostData.amount}
                                                                onChange={(e) => setNewCostData({ ...newCostData, amount: e.target.value })}
                                                                placeholder={t('leads.forms.edit_offer.placeholders.amount', '0.00')}
                                                                className="pr-8 text-sm"
                                                            />
                                                            <span className="text-muted-foreground absolute top-1/2 right-3 -translate-y-1/2 text-sm">
                                                                €
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="w-full md:w-36">
                                                        <DatePicker
                                                            value={newCostData.date_incurred ? formatDate(newCostData.date_incurred) : ''}
                                                            onValueChange={(value) =>
                                                                setNewCostData({ ...newCostData, date_incurred: parseDateValue(value) })
                                                            }
                                                            placeholder={t('common.date', 'Date')}
                                                        />
                                                    </div>
                                                    <div className="flex w-full items-center justify-end md:w-10">
                                                        <Button type="button" variant="link" size="sm" onClick={handleAddCostLine} className="gap-2">
                                                            <Plus className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* File Upload Section */}
                        {offer.status === 'pending' && (
                            <div className="border-border space-y-2 border-t pt-6">
                                <Label>{t('leads.forms.edit_offer.labels.attachments', 'Attachments (PDF, DOC, DOCX, XLS, XLSX)')}</Label>

                                {/* Existing Files */}
                                {offer.files && offer.files.length > 0 && (
                                    <div className="mb-4 space-y-2">
                                        <Label className="text-muted-foreground text-sm">
                                            {t('leads.forms.edit_offer.labels.existing_files', 'Existing Files')}
                                        </Label>
                                        <div className="space-y-2">
                                            {offer.files.map((file) => (
                                                <div key={file.id} className="bg-muted flex items-center justify-between rounded p-2">
                                                    <div className="flex items-center gap-2">
                                                        <File className="h-4 w-4" />
                                                        <span className="text-sm">{file.file_name}</span>
                                                        <span className="text-muted-foreground text-xs">({formatFileSize(file.file_size)})</span>
                                                    </div>
                                                    <Button type="button" variant="ghost" size="sm" onClick={() => handleDownload(file)}>
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Upload New Files */}
                                <div className="border-muted rounded-lg border-2 border-dashed p-4">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                                        <Upload className="mr-2 h-4 w-4" />
                                        {t('leads.forms.edit_offer.buttons.upload_files', 'Upload Files (Max 10 files total, 10MB each)')}
                                    </Button>
                                </div>
                                {selectedFiles.length > 0 && (
                                    <div className="mt-2 space-y-2">
                                        <Label className="text-muted-foreground text-sm">
                                            {t('leads.forms.edit_offer.labels.new_files_to_upload', 'New Files to Upload')}
                                        </Label>
                                        {selectedFiles.map((file, index) => (
                                            <div key={index} className="bg-muted flex items-center justify-between rounded p-2">
                                                <div className="flex items-center gap-2">
                                                    <File className="h-4 w-4" />
                                                    <span className="text-sm">{file.name}</span>
                                                    <span className="text-muted-foreground text-xs">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                                </div>
                                                <Button type="button" variant="ghost" size="sm" onClick={() => removeFile(index)}>
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Show existing files for non-pending offers */}
                        {offer.status !== 'pending' && offer.files && offer.files.length > 0 && (
                            <div className="border-border space-y-2 border-t pt-6">
                                <Label>{t('leads.forms.edit_offer.labels.attachments', 'Attachments')}</Label>
                                <div className="space-y-2">
                                    {offer.files.map((file) => (
                                        <div key={file.id} className="bg-muted flex items-center justify-between rounded p-2">
                                            <div className="flex items-center gap-2">
                                                <File className="h-4 w-4" />
                                                <span className="text-sm">{file.file_name}</span>
                                                <span className="text-muted-foreground text-xs">({formatFileSize(file.file_size)})</span>
                                            </div>
                                            <Button type="button" variant="ghost" size="sm" onClick={() => handleDownload(file)}>
                                                <Download className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" className="w-full md:w-auto" onClick={handleClose} disabled={processing}>
                            {t('leads.forms.edit_offer.buttons.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" className="w-full md:w-auto" disabled={isSubmitting || processing || offer.status !== 'pending'}>
                            {isSubmitting || processing
                                ? t('leads.forms.edit_offer.buttons.saving', 'Saving...')
                                : t('leads.forms.edit_offer.buttons.update_offer', 'Update Offer')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
